import { Component, EventEmitter, Output, OnDestroy, OnInit } from '@angular/core';
import { CustomersService } from '../../../services/customers.service';
import { Customer } from '../../../models/Customer';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface CustomerSelection {
  id: number;
  nome: string;
  cpfCnpj: string;
  telefone: string;
  email: string;
}

@Component({
  selector: 'app-customer-selection-modal',
  templateUrl: './customer-selection-modal.component.html',
  styleUrls: ['./customer-selection-modal.component.css']
})
export class CustomerSelectionModalComponent implements OnInit, OnDestroy {
  @Output() customerSelected = new EventEmitter<CustomerSelection>();
  @Output() closeModal = new EventEmitter<void>();

  showModal: boolean = false;
  searchTerm: string = '';
  customers: CustomerSelection[] = [];
  filteredCustomers: CustomerSelection[] = [];
  selectedCustomer: CustomerSelection | null = null;
  isLoading = false;

  // Paginação
  currentPage: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  totalPages: number = 0;
  
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private customersService: CustomersService) {}

  ngOnInit(): void {
    // Configurar debounce para busca
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.currentPage = 1;
      this.selectedCustomer = null;

      if (searchTerm && searchTerm.length >= 2) {
        this.loadCustomers(searchTerm);
        return;
      }

      if (searchTerm.length === 0) {
        this.loadCustomers();
        return;
      }

      this.customers = [];
      this.filteredCustomers = [];
      this.totalCount = 0;
      this.totalPages = 0;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Helper para usar Math.min no template
   */
  get Math() {
    return Math;
  }

  /**
   * Carrega todos os clientes (primeira página)
   */
  loadCustomers(search?: string): void {
    this.isLoading = true;
    const term = (search ?? this.searchTerm ?? '').trim();

    const req$ = term.length >= 2
      ? this.customersService.searchByCompanyNamePaged(term, { page: this.currentPage, pageSize: this.pageSize })
      : this.customersService.getAllPaged({ page: this.currentPage, pageSize: this.pageSize });

    req$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const list = Array.isArray(result?.data) ? result.data : [];
          this.customers = list.map(c => this.mapToCustomerSelection(c));
          this.filteredCustomers = [...this.customers];
          this.totalCount = result?.totalRecords ?? this.customers.length;
          this.totalPages = result?.totalPages ?? 1;
          this.currentPage = result?.page ?? this.currentPage;
          this.pageSize = result?.pageSize ?? this.pageSize;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.customers = [];
          this.filteredCustomers = [];
          this.totalCount = 0;
          this.totalPages = 0;
        }
      });
  }

  /**
   * Mapeia Customer para CustomerSelection
   */
  private mapToCustomerSelection(customer: Customer): CustomerSelection {
    return {
      id: customer.id,
      nome: customer.name || '',
      cpfCnpj: customer.documentNumber || '',
      telefone: customer.primaryPhone || '',
      email: customer.email || ''
    };
  }

  open(): void {
    this.showModal = true;
    this.searchTerm = '';
    this.selectedCustomer = null;
    this.currentPage = 1;
    this.totalCount = 0;
    this.totalPages = 0;
    this.loadCustomers();
  }

  close(): void {
    this.showModal = false;
    this.closeModal.emit();
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  selectCustomer(customer: CustomerSelection): void {
    this.selectedCustomer = customer;
  }

  confirmSelection(): void {
    if (this.selectedCustomer && !this.isLoading) {
      this.customerSelected.emit(this.selectedCustomer);
      this.close();
    }
  }

  /**
   * Vai para a próxima página
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.selectedCustomer = null;
      this.loadCustomers();
    }
  }

  /**
   * Vai para a página anterior
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.selectedCustomer = null;
      this.loadCustomers();
    }
  }

  /**
   * Vai para uma página específica
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.selectedCustomer = null;
      this.loadCustomers();
    }
  }

  /**
   * Gera array de números de página para exibição
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    const halfWindow = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, this.currentPage - halfWindow);
    let endPage = Math.min(this.totalPages, this.currentPage + halfWindow);

    if (this.currentPage <= halfWindow) {
      endPage = maxPagesToShow;
    }

    if (this.currentPage >= this.totalPages - halfWindow) {
      startPage = this.totalPages - maxPagesToShow + 1;
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }
}

